import LuissSearch from './luiss-search-common.js';

(function (Drupal, LuissSearch) {

  Drupal.behaviors.lcInternalSearch = {
    attach(context, settings) {
      const lcInternalSearch = once('internal-search', document.getElementsByClassName('lc-internal-search'), context);
      for (let i = 0; i < lcInternalSearch.length; i++) {
        // Wrap each search instance setup in an async IIFE so we can await
        // pre-flight Typesense facet requests (e.g. prefetch_all_values) before
        // the InstantSearch instance starts. The outer for-loop is intentionally
        // synchronous so that all instances are kicked off in parallel.
        // NOTE: relies on block-scoped `let i` so each async closure captures
        // its own iteration index. Do not change to `var`.
        (async () => {
          const index = lcInternalSearch[i].dataset.internalSearchIndex;
          const config = settings.luiss_ds.internal_search[index];
          if (!config) {
            return;
          }

          const luissSearch = new LuissSearch(
            config.credentials,
            config.exclude_fields,
            JSON.parse(config.not_found_messages),
            config.pagination ? JSON.parse(config.pagination) : null,
            config.sort_by || null,
            config.filter_by || null
          );

          // Build the Typesense pinned_hits string from the node IDs passed via
          // config. These are used to unconditionally promote specific documents
          // to the top of results when no query or filter is active. The format
          // per Typesense is a comma-separated list of
          // "{document_id}:{1-based-position}" pairs. Document IDs follow the
          // Search API Typesense convention: the item ID "entity:node/{nid}:{lang}"
          // with "/" replaced by "-" → "entity:node-{nid}:{lang}".
          const pinnedHitsIds = config.pinned_hits_ids || null;
          let defaultPinnedHitsStr = null;
          if (pinnedHitsIds && pinnedHitsIds.length > 0) {
            const lang = settings.path.currentLanguage;
            defaultPinnedHitsStr = pinnedHitsIds
            .map((nid, pos) => `entity:node-${nid}:${lang}:${pos + 1}`)
            .join(',');
          }

          // When sort_by is configured, derive a relevance-first variant by
          // prepending _text_match:desc. The searchFunction will switch between
          // the two depending on whether a text query is active.
          const defaultSortBy = config.sort_by || null;
          const searchSortBy = defaultSortBy ? `_text_match:desc,${defaultSortBy}` : null;

          // Build base filters (always applied regardless of user interaction).
          // The langcode filter is added by default so each index only returns
          // documents in the current interface language. Indexes whose documents
          // are language-agnostic (e.g. publications) opt out via
          // differentiate_by_langcode: false so results are not filtered by lang.
          const baseFilters = [];
          if (config.differentiate_by_langcode !== false) {
            baseFilters.push(`langcode:${settings.path.currentLanguage}`);
          }
          if (config.filter_by) {
            baseFilters.push(config.filter_by);
          }

          // Initial filters: include default_filter_by since no refinements are
          // active at page load. When the user interacts, searchFunction will
          // drop it dynamically.
          const initialFilters = config.default_filter_by
            ? [...baseFilters, config.default_filter_by]
            : [...baseFilters];

          const hitsPerPage = config.hits_per_page || 12;
          const configureOptions = {
            hitsPerPage: hitsPerPage,
          };

          if (initialFilters.length > 0) {
            configureOptions.filters = initialFilters.join(' && ');
          }

          // Build a single searchFunction that handles all dynamic behaviours:
          //   1. Toggle pinned_hits: active when no query/filter, cleared otherwise.
          //   2. Switch sort_by: date order when browsing, relevance-first when
          //      a text query is typed.
          //   3. Toggle default_filter_by: applied when no user interaction,
          //      dropped as soon as any filter or query becomes active.
          // All three are optional; the function is only installed when at least
          // one is configured. getSearchClient() copies searchFunction at call
          // time, so the function must be fully assembled before that call.
          const searchOptions = {};
          if (defaultPinnedHitsStr || searchSortBy || config.default_filter_by) {
            searchOptions.searchFunction = function (helper) {
              if (defaultPinnedHitsStr) {
                const shouldPin = !luissSearch.hasActiveRefinements(helper);
                luissSearch.setPinnedHits(shouldPin ? defaultPinnedHitsStr : null);
              }
              if (searchSortBy) {
                const hasQuery = !!(helper.state.query && helper.state.query.trim() !== '');
                luissSearch.setSortBy(hasQuery ? searchSortBy : defaultSortBy);
              }
              if (config.default_filter_by) {
                const isDefault = !luissSearch.hasActiveRefinements(helper);
                luissSearch.isDefaultView = isDefault;
                const dynamicFilters = isDefault
                  ? [...baseFilters, config.default_filter_by]
                  : [...baseFilters];
                // Use helper.state.setQueryParameter (SearchParameters method) instead
                // of helper.setQueryParameter (helper method) to avoid firing a
                // _change event, which would queue a second search and reset the page.
                helper.state = helper.state.setQueryParameter('filters', dynamicFilters.join(' && '));
              }
              helper.search();
            };
          }

          // Pre-fetch all possible facet values for select filters that
          // explicitly opt in via prefetch_all_values: true. Used in
          // combination with static_in_default_view_only to show all values
          // (including past) on the default view, then switch to live faceted
          // items when the user interacts with filters.
          // Each call uses per_page=0 — Typesense returns no document bodies,
          // only facet counts — making it very fast and the payload tiny.
          const parsedFilterConfig = config.filters ? JSON.parse(config.filters) : null;
          const staticFacets = {};
          if (config.filters) {
            await Promise.all(
              Object.entries(parsedFilterConfig)
              .filter(([, f]) => f.type === 'select' && f.prefetch_all_values === true)
              .map(async ([key, f]) => {
                try {
                  staticFacets[key] = await luissSearch.prefetchFacetValues(index, f.attribute, baseFilters);
                }
                catch (e) {
                  // Non-critical: fall back to live InstantSearch items only.
                  console.warn(`prefetchFacetValues failed for ${f.attribute}:`, e);
                }
              })
            );
          }

          // Public names the filter attributes are exposed as in the URL. A
          // filter is masked by its own config key (category), unless it sets
          // an explicit url_alias. Attributes without an entry keep their raw
          // Typesense name in the URL. Both the aliases and the masking of the
          // index name apply only when the environment enables it.
          searchOptions.maskUrls = Boolean(config.mask_urls);
          if (parsedFilterConfig && searchOptions.maskUrls) {
            searchOptions.urlAliases = Object.fromEntries(
              Object.entries(parsedFilterConfig)
              .filter(([, f]) => f.attribute)
              .map(([key, f]) => [f.attribute, f.url_alias || key])
            );
          }

          const search = luissSearch.getSearchClient(index, searchOptions);

          lcInternalSearch[i].__lcSearchInstance = search;

          // Widgets connectors
          const customSearchBox = luissSearch.getCustomSearchBoxConnector();
          const customHits = luissSearch.getCustomHitsConnector(hit => hit.rendered_item);
          const customMenuSelect = luissSearch.getCustomMenuSelectConnector();
          const customToggleRefinement = luissSearch.getCustomToggleRefinementConnector();
          const customPagination = luissSearch.getCustomPaginationConnector();

          search.addWidgets([
            instantsearch.widgets.configure({
              ...configureOptions,
            }),
            customSearchBox({
              container: `#lc-internal-search__searchbox-${index}`,
              placeholder: config.search_placeholder,
              autofocus: false,
              queryHook: (function() {
                let timeoutId;
                return function(query, search) {
                  clearTimeout(timeoutId);
                  timeoutId = setTimeout(() => search(query), 500);
                };
              })(),
            }),
            customHits({
              container: `#lc-internal-search__hits-${index}`,
              headingLevel: config.heading_level || null,
              counterContainerSelector: `#lc-internal-search__results-counter-${index}`,
              counterSingularLabel: config.counter_singular_label,
              counterPluralLabel: config.counter_plural_label,
              notFoundMessages: JSON.parse(config.not_found_messages),
              icPlaceholders: settings.luiss_ds.ic_placeholders || null
            }),
            customPagination({
              container: `#lc-internal-search__pagination-${index}`,
              scrollTo: `#lc-internal-search__hits-${index}`,
              hitsPerPage: hitsPerPage,
              padding: 2,
            }),
          ]);

          // Add filters if any.
          if (parsedFilterConfig) {
            Object.keys(parsedFilterConfig).forEach((key) => {
              const f = parsedFilterConfig[key];
              if (f.type === 'checkbox') {
                search.addWidgets([
                  customToggleRefinement({
                    container: f.container,
                    attribute: f.attribute,
                    on: f.on ?? 'true',
                    label: f.label,
                    toggleTarget: f.toggle_target ?? null,
                  }),
                ]);
              }
              else {
                const selectParams = {
                  container: f.container,
                  attribute: f.attribute,
                  placeholder: f.placeholder,
                  label: f.label,
                  dependsOn: f.depends_on && parsedFilterConfig[f.depends_on] ? parsedFilterConfig[f.depends_on].attribute : null,
                  // Collect attributes of filters that depend on this one, so
                  // the change handler can clear them when the parent changes.
                  dependentAttributes: Object.values(parsedFilterConfig)
                    .filter(other => other.depends_on === key)
                    .map(other => other.attribute),
                  staticItems: staticFacets[key] || null,
                  staticInDefaultViewOnly: f.static_in_default_view_only || false,
                  facetExcludes: f.facet_excludes || null,
                  searchIndex: index,
                  baseFilters: baseFilters,
                  monthNameFormat: f.month_name_format || null,
                  valueLabels: f.value_labels || null,
                  operator: 'or',
                  limit: 50,
                };
                search.addWidgets([customMenuSelect(selectParams)]);
              }
            });
          }

          // Track first render to remove loader
          let isFirstRender = true;

          // Update Toggle Button State (Active Filters)
          search.on('render', () => {
            const container = lcInternalSearch[i];

            // Remove filters loader on first render
            if (isFirstRender) {
              const filtersWrapper = container.querySelector('.lc-internal-search__filters-wrapper');
              if (filtersWrapper) {
                filtersWrapper.classList.remove('is-loading');
              }
              isFirstRender = false;
            }

            const hasActiveFilters = luissSearch.hasActiveRefinements(search.helper);

            const toggleBtn = container.querySelector('.lc-internal-search__toggle');
            const toggleTextSpan = container.querySelector('.lc-internal-search__toggle-text');

            if (toggleBtn) {
              if (hasActiveFilters) {
                toggleBtn.classList.add('has-active-filters');
                if (toggleTextSpan && config.toggle_active_label) {
                  toggleTextSpan.textContent = config.toggle_active_label;
                }
              } else {
                toggleBtn.classList.remove('has-active-filters');
                if (toggleTextSpan && config.toggle_label) {
                  toggleTextSpan.textContent = config.toggle_label;
                }
              }
            }

            window.searchStatus = 'idle';
          });

          search.on('search', () => {
            window.searchStatus = 'loading';
          });

          search.start();
        })(); // end async IIFE
      }

      // Mobile filters toggle logic.
      const searchContainers = once('internal-search-toggle', document.querySelectorAll('.lc-internal-search'), context);
      searchContainers.forEach(container => {
        const toggleBtn = container.querySelector('.lc-internal-search__toggle');
        const filtersWrapper = container.querySelector('.lc-internal-search__filters-wrapper');
        const closeBtn = container.querySelector('.lc-internal-search__header-close');
        const applyBtn = container.querySelector('.lc-internal-search__actions-apply');
        const resetBtn = container.querySelector('.lc-internal-search__actions-reset');

        const closeFilters = () => {
          container.classList.remove('is-internal-search-filters-open');
          document.body.classList.remove('is-internal-search-filters-open');
          container.classList.add('lc-internal-search--is-closing');
          setTimeout(() => {
            container.classList.remove('lc-internal-search--is-closing');
          }, 300);
        };

        if (toggleBtn && filtersWrapper) {
          const toggleFilters = () => {
            if (container.classList.contains('is-internal-search-filters-open')) {
              closeFilters();
            } else {
              container.classList.add('is-internal-search-filters-open');
              document.body.classList.add('is-internal-search-filters-open');
            }
          };

          toggleBtn.addEventListener('click', (e) => {
            e.preventDefault();
            toggleFilters();
          });

          if (closeBtn) {
            closeBtn.addEventListener('click', (e) => {
              e.preventDefault();
              closeFilters();
            });
          }

          if (applyBtn) {
            applyBtn.addEventListener('click', (e) => {
              e.preventDefault();
              closeFilters();
            });
          }

          if (resetBtn) {
            resetBtn.addEventListener('click', (e) => {
              e.preventDefault();
              const searchInstance = container.__lcSearchInstance;
              if (searchInstance && searchInstance.helper) {
                searchInstance.helper.clearRefinements();
                searchInstance.helper.setQuery('');
                searchInstance.helper.search();
              }
            });
          }
        }
      });
    },
  };
})(Drupal, LuissSearch);
